import map from "lodash/map";
import CheerzApiCart from "@Types/api/cart";
import CartContent from "@Types/cart";
import { convertFromApiToPrice, convertFromPriceContentToApi } from "./price";
import {
  convertFromApiToShippingOption,
  convertFromShippingOptionContentToApi,
} from "./shippingOptions";

export function convertFromCartContentToApi(cartContent: CartContent): CheerzApiCart {
  const cartJson: CheerzApiCart = {
    articles: map(cartContent.articles, (article) => {
      return {
        additional_options: map(article.additionalOptions, (option) => {
          return {
            name: option.name ? option.name : null,
            option_value_tag: option.optionValueTag,
            product_option_tag: option.productOptionTag,
            quantity: option.quantity,
            tag: option.tag,
            total: convertFromPriceContentToApi(option.total),
          };
        }),

        content_device_tag: article.contentDeviceTag,
        created_at: article.createdDate,
        currency: article.currency,
        gift: article.gift,
        id: article.id,

        product: {
          article_behavior_tag: article.product.articleBehaviorTag,
          deprecated: article.product.deprecated,
          name: article.product.name,
          out_of_stock: article.product.outOfStock,
          price_cents: article.product.priceCents,
          product_image: {
            "1x": article.product.productImage.small,
            "2x": article.product.productImage.medium,
            "3x": article.product.productImage.big,
          },
          skip_selection: article.product.skipSelection,
          stat_product_group_tag: article.product.statProductGroupTag,
          tag: article.product.tag,
          thumb: article.product.thumb ? article.product.thumb : null,
        },

        quantity: article.quantity,
        recovered: article.recovered,
        selection_content: article.selectionContent ? article.selectionContent : null,
        total: convertFromPriceContentToApi(article.total),
        updated_at: article.updatedDate,
      };
    }),

    cart_modifiers: map(cartContent.cartModifiers, (modifier) => {
      return {
        articles: modifier.articles,
        discount_rate: modifier.discountRate,
        tag: modifier.tag,
        total_with_discount: convertFromPriceContentToApi(modifier.totalWithDiscount),
        total_without_discount: convertFromPriceContentToApi(modifier.totalWithoutDiscount),
      };
    }),

    currency: cartContent.currency,
    delivery_dates: cartContent.deliveryDates,
    gift_note: {
      available: cartContent.giftNote.available,
      content: cartContent.giftNote.content ? cartContent.giftNote.content : null,
    },
    locked_during_payment: cartContent.lockedDuringPayment,
    order_number: cartContent.orderNumber,
    promo_code: cartContent.promoCode ? cartContent.promoCode : null,
    promo_total: convertFromPriceContentToApi(cartContent.promoTotal),
    revenue: cartContent.revenue,
    shippable: cartContent.shippable,
    shipping_options: {
      express: convertFromShippingOptionContentToApi(cartContent.shippingOptions.home.express),
      normal: convertFromShippingOptionContentToApi(cartContent.shippingOptions.home.normal),
    },
    shipping_total: convertFromPriceContentToApi(cartContent.shippingTotal),
    sub_total: convertFromPriceContentToApi(cartContent.subTotal),
    tax: cartContent.tax,
    total: convertFromPriceContentToApi(cartContent.total),
  };

  if (cartContent.shippingOptions.mondialRelay) {
    cartJson.shipping_options.mondial_relay = {
      express: convertFromShippingOptionContentToApi(
        cartContent.shippingOptions.mondialRelay.express
      ),
      normal: convertFromShippingOptionContentToApi(
        cartContent.shippingOptions.mondialRelay.normal
      ),
    };
  }

  return cartJson;
}

export function convertFromApiToCartContent(jsonContent: CheerzApiCart): CartContent {
  const cartContent: CartContent = {
    articles: map(jsonContent.articles, (article) => {
      return {
        additionalOptions: map(article.additional_options, (option) => {
          return {
            name: option.name ? option.name : undefined,
            optionValueTag: option.option_value_tag,
            productOptionTag: option.product_option_tag,
            quantity: option.quantity,
            tag: option.tag,
            total: convertFromApiToPrice(option.total),
          };
        }),

        contentDeviceTag: article.content_device_tag,
        createdDate: article.created_at,
        currency: article.currency,
        gift: article.gift ? true : false,
        id: article.id,

        product: {
          articleBehaviorTag: article.product.article_behavior_tag,
          deprecated: article.product.deprecated,
          name: article.product.name,
          outOfStock: article.product.out_of_stock,
          priceCents: article.product.price_cents,
          productImage: {
            big: article.product.product_image["3x"],
            medium: article.product.product_image["2x"],
            small: article.product.product_image["1x"],
          },
          skipSelection: article.product.skip_selection,
          statProductGroupTag: article.product.stat_product_group_tag,
          tag: article.product.tag,
          thumb: article.product.thumb ? article.product.thumb : undefined,
        },

        quantity: article.quantity,
        recovered: article.recovered,
        selectionContent: article.selection_content ? article.selection_content : undefined,
        total: convertFromApiToPrice(article.total),
        updatedDate: article.updated_at,
      };
    }),

    cartModifiers: map(jsonContent.cart_modifiers, (modifier) => {
      return {
        articles: modifier.articles,
        discountRate: modifier.discount_rate,
        tag: modifier.tag,
        totalWithDiscount: convertFromApiToPrice(modifier.total_with_discount),
        totalWithoutDiscount: convertFromApiToPrice(modifier.total_without_discount),
      };
    }),

    currency: jsonContent.currency,
    deliveryDates: jsonContent.delivery_dates,
    giftNote: {
      available: jsonContent.gift_note.available,
      content: jsonContent.gift_note.content ? jsonContent.gift_note.content : undefined,
    },
    lockedDuringPayment: jsonContent.locked_during_payment,
    orderNumber: jsonContent.order_number,
    promoCode: jsonContent.promo_code ? jsonContent.promo_code : undefined,
    promoTotal: convertFromApiToPrice(jsonContent.promo_total),
    revenue: jsonContent.revenue,
    shippable: jsonContent.shippable,
    shippingOptions: {
      home: {
        express: convertFromApiToShippingOption(jsonContent.shipping_options.express),
        normal: convertFromApiToShippingOption(jsonContent.shipping_options.normal),
      },
    },
    shippingTotal: convertFromApiToPrice(jsonContent.shipping_total),
    subTotal: convertFromApiToPrice(jsonContent.sub_total),
    tax: jsonContent.tax,
    total: convertFromApiToPrice(jsonContent.total),
  };

  if (jsonContent.shipping_options.mondial_relay) {
    cartContent.shippingOptions.mondialRelay = {
      express: convertFromApiToShippingOption(jsonContent.shipping_options.mondial_relay.express),
      normal: convertFromApiToShippingOption(jsonContent.shipping_options.mondial_relay.normal),
    };
  }

  return cartContent;
}
